import { forwardRef, useEffect, useState, useRef } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import {
  ServicesRepository,
  type Services as ServicesType,
} from "@repo/fetch-repository/services";
import { Link } from "@repo/i18n/routing";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { useApp } from "@repo/app/useApp";
import Autoplay from "embla-carousel-autoplay";
import type { AutoplayType } from "embla-carousel-autoplay";
import type { CarouselApi } from "../components/ui/carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import useIsDesktop from "../hooks/use-is-desktop";

export const servicesCarouselDefinition: ComponentSettingsProps = {
  title: {
    type: "string",
    label: "عنوان",
  },
  number: {
    type: "string",
    label: "شماره",
  },
  sideText: {
    type: "string",
    label: "متن شماره",
  },
};

export interface ServicesCarouselProps {
  count: number;
  title: string;
  number: string;
  sideText: string;
}

const ServicesCarouselComponent = forwardRef<
  HTMLDivElement,
  ServicesCarouselProps
>((props, ref) => {
  const app = useApp();
  const isEditor = app.rendererMode === "editor";
  const [services, setServices] = useState<ServicesType[] | []>([]);
  const router = useRouter();
  const locale =
    app.rendererMode === "editor"
      ? (router.query.locale as string)
      : router.locale;

  const plugin = useRef<AutoplayType>(
    Autoplay({
      delay: 5000,
    })
  );
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const [thumbnailApi, setThumbnailApi] = useState<CarouselApi>();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    mainApi.on("select", () => {
      const index = mainApi.selectedScrollSnap();
      thumbnailApi?.scrollTo(index);
    });
  }, [mainApi, thumbnailApi]);

  useEffect(() => {
    const repository: ServicesRepository = new ServicesRepository(locale);
    void repository
      .getMany({
        with: ["main_image"],
      })
      .then((res) => {
        if (res.succeeded && res.data) {
          setServices(res.data);
        }
      });
  }, [locale]);

  return (
    <>
      {props.sideText ? (
        <div className="absolute ltr:left-10 rtl:right-10 top-3/4 lg:flex hidden">
          <div className="size-full relative flex items-center justify-center">
            <div className="absolute rtl:rotate-90 ltr:-rotate-90 font-thin lg:text-3xl text-xl px-2 text-gray-300 before:content-[''] before:inline-block before:bg-gray-300 before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-gray-300 after:h-px after:lg:w-14 after:w-10 flex items-center">
              <span className="mx-2 uppercase rtl:-mt-3 whitespace-nowrap rtl:font-doran rtl:font-medium">
                {props.sideText}
              </span>
            </div>
          </div>
        </div>
      ) : null}
      <div
        className="relative grid grid-cols-1 lg:grid-cols-2 gap-10"
        ref={ref}
      >
        {services.length > 0 && (
          <>
            <div className="flex flex-col gap-4 w-full rtl:lg:order-1">
              <Carousel
                className="w-full"
                dir="ltr"
                onMouseEnter={plugin.current.stop}
                onMouseLeave={plugin.current.reset}
                plugins={[plugin.current]}
                setApi={setMainApi}
              >
                <CarouselContent>
                  {services.map((service) => (
                    <CarouselItem className="basis-1/4" key={service.id}>
                      <img
                        alt={service.name}
                        className="w-full lg:mb-10 mb-4"
                        onError={(e) => {
                          e.currentTarget.src = "/images/placeholder.png";
                        }}
                        src={
                          !Array.isArray(service.main_image.data)
                            ? service.main_image.data.url
                            : ""
                        }
                      />
                      <Link
                        className="lg:text-2xl text-base font-helvetica font-[100] text-[#131d29] text-start transition-colors hover:text-[#FF7700] rtl:font-doran rtl:font-medium"
                        href={`/services/${service.id}/${encodeURIComponent(
                          service.name.replace(/\s+/g, "-").toLowerCase() || ""
                        )}`}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          if (isEditor) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {service.name}
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className="flex flex-col ml-auto w-full">
              <div className="absolute lg:relative -top-16 ltr:-left-6 rtl:right-16 md:left-16 flex justify-end rtl:justify-start">
                <div className="inline-block relative">
                  <div
                    className="box-content text-center text-[100px] md:text-[200px] lg:text-[250.28px] text-[#FF770033] font-helvetica"
                    style={{
                      fontWeight: "250",
                      letterSpacing: "-0.05em",
                      lineHeight: "240.35px",
                    }}
                  >
                    {props.number}
                  </div>

                  <div
                    className="inline-block left-1/2 rtl:font-doran rtl:font-bold font-[250] rtl:text-[#f70] absolute top-0 text-[19px] md:text-[29px] lg:text-[53.35px] font-helvetica text-[#6A6A6A] uppercase"
                    style={{
                      transform: props.number
                        ? "translateY(70px) translateX(-50%)"
                        : "translateY(70px) translateX(-100%)",
                      lineHeight: "57.09px",
                    }}
                  >
                    {props.title}
                  </div>
                </div>
              </div>
              <Carousel
                className="w-full"
                dir="ltr"
                opts={{
                  axis: isDesktop ? "x" : "y",
                }}
                orientation={isDesktop ? "horizontal" : "vertical"}
                setApi={setThumbnailApi}
              >
                <CarouselContent className="h-[200px] lg:h-auto">
                  {services.map((service, index) => (
                    <CarouselItem
                      className="lg:basis-1/5 basis-1/3 group pl-0 pt-0"
                      key={service.id}
                    >
                      <button
                        className="cursor-pointer hover:bg-gray-100 p-4 lg:p-0 lg:write-vertical-left lg:h-[492px] lg:text-end px-10 lg:last:border-r lg:group-first-of-type:border-l lg:border-0 lg:group-last:border-b-0 border-t group-last:border-b border-[#ff7700] lg:[writing-mode:vertical-lr] lg:[textOrient:mixed] w-full h-full outline-none"
                        onClick={() => {
                          mainApi?.scrollTo(index);
                        }}
                        type="button"
                      >
                        <p className="text-xl lg:text-2xl font-medium rtl:font-medium uppercase rtl:font-doran pb-4">
                          {service.name}
                        </p>
                      </button>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </>
        )}
      </div>
    </>
  );
});

ServicesCarouselComponent.displayName = "ServicesCarousel";

export const ServicesCarousel = observer(
  ServicesCarouselComponent
) as unknown as ComponentType<ServicesCarouselProps>;
export const servicesCarouselSettings: ComponentSettings = {
  name: "EServicesCarousel",
  props: servicesCarouselDefinition,
  label: "اسلایدر سرویس ها",
  widgets: {
    label: "اسلایدر سرویس ها",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EServicesCarousel", external: true }),
        props: {
          count: t.literal({
            value: 3,
          }),
        },
      }),
  },
};

ServicesCarousel.componentSettings = servicesCarouselSettings;
